import { useEffect, useState, type FormEvent } from 'react';
import { testDiscord, updateSettings, type SignInSettingsView } from '../../api/admin';
import { fetchDiscordBot, type DiscordBotView } from '../../api/changelog';
import type { Meta } from '../../api/meta';
import { fetchServers, type ServersResponse, type ServerView } from '../../api/servers';
import { ca } from '../../i18n/ca';
import { ChangelogCard, DiscordBotCard } from './DiscordBotCards';
import { PanelCard } from './PanelCard';
import { RoadmapSourceCard } from './RoadmapSourceCard';
import { discordRefusalText, errorText } from './shared';

interface Props {
  settings: SignInSettingsView | null;
  meta: Meta;
  onSaved: (s: SignInSettingsView) => void;
  onError: (text: string | null) => void;
}

/** Integrations tab: the sign-in doors, the Discord webhook and the panel, as three cards. */
export function IntegrationsTab({ settings, meta, onSaved, onError }: Props) {
  const [discord, setDiscord] = useState<{ text: string; ok: boolean } | null>(null);
  // The bot's own settings, in one call; null until it answers, and null for
  // good on an installation whose platform is older than the bot.
  const [bot, setBot] = useState<DiscordBotView | null>(null);
  // One call for both cards below: the picker names a server, and the panel card
  // says whether the panel actually answered just now — which is not the same
  // question as whether it is configured.
  const [panelState, setPanelState] = useState<ServersResponse | null>(null);
  useEffect(() => {
    fetchDiscordBot()
      .then(setBot)
      .catch(() => setBot(null));
    fetchServers()
      .then(setPanelState)
      .catch(() => setPanelState(null));
  }, []);

  function probeDiscord() {
    testDiscord()
      .then((r) =>
        setDiscord(
          !r.configured
            ? { text: ca.discordUnset, ok: false }
            : {
                ok: r.ok,
                text: r.channels
                  .map((c) => {
                    const label = ca.discordChannels[c.channel] ?? c.channel;
                    if (!c.configured) return `${label}: ${ca.discordUnset}`;
                    if (c.ok) return `${label}: ${ca.discordOk}`;
                    // What Discord said, when it said something.
                    return `${label}: ${discordRefusalText(c.discord) ?? ca.discordFailed}`;
                  })
                  .join(' · '),
              },
        ),
      )
      .catch(() => setDiscord({ text: ca.discordFailed, ok: false }));
  }

  return (
    <section className="stack" aria-label={ca.adminUi.tabs.integrations}>
      <p className="muted small">{ca.adminUi.intros.integrations}</p>
      <div className="dashboard masonry">
        {settings && (
          <SignInDoors
            settings={settings}
            publicBaseUrl={meta.publicBaseUrl}
            onSaved={onSaved}
            onError={onError}
          />
        )}
        {settings && (
          <ServerDefaults
            settings={settings}
            servers={panelState?.servers ?? null}
            onSaved={onSaved}
            onError={onError}
          />
        )}
        {bot && <DiscordBotCard view={bot} onSaved={setBot} onError={onError} />}
        {bot && <ChangelogCard view={bot} onSaved={setBot} onError={onError} />}
        <RoadmapSourceCard />
        <section className="card" aria-label={ca.adminUi.discordCard}>
          <h2>
            <i className="bi bi-discord" aria-hidden="true" /> {ca.adminUi.discordCard}
          </h2>
          <p className="muted small">{ca.adminUi.discordCardIntro}</p>
          <div className="row actions">
            <button type="button" onClick={probeDiscord}>
              <i className="bi bi-send" aria-hidden="true" /> {ca.discordTest}
            </button>
          </div>
          {discord && (
            <p className={`notice ${discord.ok ? 'ok' : 'bad'}`} role="status">
              {discord.text}
            </p>
          )}
        </section>
        <PanelCard
          meta={meta}
          counts={
            panelState
              ? ca.adminUi.panelCounts(
                  panelState.servers.length,
                  panelState.budget.used,
                  panelState.budget.max,
                )
              : null
          }
        />
      </div>
    </section>
  );
}

/**
 * What a server the platform creates on the panel is born with: the two passwords
 * — the admin one, for whoever directs the session, and the one players type to
 * join — and the server the rest of its settings are copied from. Neither password
 * is ever read back, so an empty field means "keep the stored one".
 */
function ServerDefaults({
  settings,
  servers,
  onSaved,
  onError,
}: {
  settings: SignInSettingsView;
  /** The panel's servers, to name one by name; null while they have not answered. */
  servers: ServerView[] | null;
  onSaved: (s: SignInSettingsView) => void;
  onError: (text: string | null) => void;
}) {
  const [adminPassword, setAdminPassword] = useState('');
  const [playerPassword, setPlayerPassword] = useState('');
  const [saved, setSaved] = useState(false);
  const [reference, setReference] = useState<number>(settings.referenceServerId ?? 0);

  async function save(event: FormEvent) {
    event.preventDefault();
    try {
      const view = await updateSettings({
        ...(adminPassword ? { serverAdminPassword: adminPassword } : {}),
        ...(playerPassword ? { serverPlayerPassword: playerPassword } : {}),
        // Only when it changed: the endpoint takes a partial change, and sending a 0
        // that nobody touched would clear a stored reference on a page that never
        // learned there was one.
        ...(reference !== (settings.referenceServerId ?? 0)
          ? { referenceServerId: reference }
          : {}),
      });
      setAdminPassword('');
      setPlayerPassword('');
      setSaved(true);
      onSaved(view);
      onError(null);
    } catch (err) {
      onError(errorText(err));
    }
  }

  return (
    <form className="card form" onSubmit={save} aria-label={ca.adminUi.serversCard}>
      <h2>
        <i className="bi bi-shield-lock" aria-hidden="true" /> {ca.adminUi.serversCard}
      </h2>
      <p className="muted small">{ca.adminUi.serversCardIntro}</p>
      <label>
        {ca.adminUi.serverAdminPassword}
        <input
          name="serverAdminPassword"
          type="password"
          autoComplete="new-password"
          value={adminPassword}
          onChange={(e) => setAdminPassword(e.target.value)}
        />
        <span className="hint">{ca.adminUi.serverAdminPasswordHint}</span>
        <span className="hint">
          {settings.serverAdminPasswordSet ? ca.adminUi.passwordStored : ca.adminUi.passwordUnset}
        </span>
      </label>
      <label>
        {ca.adminUi.serverPlayerPassword}
        <input
          name="serverPlayerPassword"
          type="password"
          autoComplete="new-password"
          value={playerPassword}
          onChange={(e) => setPlayerPassword(e.target.value)}
        />
        <span className="hint">{ca.adminUi.serverPlayerPasswordHint}</span>
        <span className="hint">
          {settings.serverPlayerPasswordSet ? ca.adminUi.passwordStored : ca.adminUi.passwordUnset}
        </span>
      </label>
      <label>
        {ca.adminUi.referenceServer}
        <select
          name="referenceServerId"
          value={String(reference)}
          onChange={(e) => setReference(Number(e.target.value))}
        >
          <option value="0">{ca.adminUi.referenceServerNone}</option>
          {(servers ?? []).map((s) => (
            <option key={s.id} value={String(s.id)}>
              {s.name}
            </option>
          ))}
          {reference > 0 && !(servers ?? []).some((s) => s.id === reference) && (
            <option value={String(reference)}>#{reference}</option>
          )}
        </select>
        <span className="hint">{ca.adminUi.referenceServerHint}</span>
      </label>
      <div className="row actions">
        <button type="submit" className="primary">
          {ca.saveSettings}
        </button>
        {saved && (
          <span className="muted small" role="status">
            {ca.settingsSaved}
          </span>
        )}
      </div>
    </form>
  );
}

/** The sign-in doors, configured here and applied at once: Steam needs nothing, Discord needs its application. */
function SignInDoors({
  settings,
  publicBaseUrl,
  onSaved,
  onError,
}: {
  settings: SignInSettingsView;
  publicBaseUrl: string;
  onSaved: (s: SignInSettingsView) => void;
  onError: (text: string | null) => void;
}) {
  const [steam, setSteam] = useState(settings.steamEnabled);
  const [steamPlayers, setSteamPlayers] = useState(settings.steamPlayersMayJoin);
  const [discordOn, setDiscordOn] = useState(settings.discordEnabled);
  const [clientId, setClientId] = useState(settings.discordClientId);
  const [clientSecret, setClientSecret] = useState('');
  const [saved, setSaved] = useState(false);
  const redirect = `${publicBaseUrl || window.location.origin}/api/auth/discord/return`;

  async function save(event: FormEvent) {
    event.preventDefault();
    try {
      const view = await updateSettings({
        steamEnabled: steam,
        steamPlayersMayJoin: steamPlayers,
        discordEnabled: discordOn,
        discordClientId: clientId,
        ...(clientSecret ? { discordClientSecret: clientSecret } : {}),
      });
      setClientSecret('');
      setSaved(true);
      onSaved(view);
      onError(null);
    } catch (err) {
      onError(errorText(err));
    }
  }

  return (
    <form className="card form" onSubmit={save} aria-label={ca.signInDoors}>
      <h2>
        <i className="bi bi-door-open" aria-hidden="true" /> {ca.signInDoors}
      </h2>
      <p className="muted small">{ca.signInDoorsIntro}</p>
      <label className="check">
        <input type="checkbox" checked={steam} onChange={(e) => setSteam(e.target.checked)} />
        {ca.steamDoor}
      </label>
      <p className="hint">{ca.steamDoorHint}</p>
      <label className="check">
        <input
          type="checkbox"
          checked={steamPlayers}
          disabled={!steam}
          onChange={(e) => setSteamPlayers(e.target.checked)}
        />
        {ca.steamPlayersJoin}
      </label>
      <p className="hint">{ca.steamPlayersJoinHint}</p>
      <label className="check">
        <input
          type="checkbox"
          checked={discordOn}
          onChange={(e) => setDiscordOn(e.target.checked)}
        />
        {ca.discordDoor}
      </label>
      <p className="hint">{ca.discordDoorHint}</p>
      <p className="hint">
        {ca.discordRedirect('')}
        <code>{redirect}</code>
      </p>
      <label>
        {ca.discordClientId}
        <input
          name="discordClientId"
          value={clientId}
          autoComplete="off"
          onChange={(e) => setClientId(e.target.value)}
        />
      </label>
      <label>
        {ca.discordClientSecret}
        <input
          name="discordClientSecret"
          type="password"
          autoComplete="new-password"
          value={clientSecret}
          onChange={(e) => setClientSecret(e.target.value)}
        />
        <span className="hint">
          {settings.discordSecretSet ? ca.discordSecretSet : ca.discordSecretUnset}
        </span>
      </label>
      {discordOn && !settings.discordConfigured && (
        <p className="notice warn">{ca.discordNotConfiguredWarning}</p>
      )}
      <div className="row actions">
        <button type="submit" className="primary">
          {ca.saveSettings}
        </button>
        {saved && (
          <span className="muted small" role="status">
            {ca.settingsSaved}
          </span>
        )}
      </div>
    </form>
  );
}
